import { ImageOff } from 'lucide-react'

import Button from '/react4xp/components/prototype/Button'

import type { HeroProps } from './types'

// Shared fallback figure for a missing/broken image entry — same-shape placeholder, never a bare/broken <img>
function ImageFallback({ className }: { className: string }) {
	return (
		<figure className={className}>
			<ImageOff className="w-10 h-10 text-brand-300" aria-hidden="true" />
		</figure>
	)
}

export default function Hero({
	overline,
	heading,
	images = [],
	primaryCta,
	secondaryCta,
}: HeroProps) {
	const [image1, image2] = images
	const hasCta = Boolean(primaryCta || secondaryCta)

	const overlineText = overline && (
		<p className="uppercase text-lg leading-normal font-normal text-brand-500">{overline}</p>
	)

	const headingText = (
		<h1 className="text-[2.5rem] md:text-5xl leading-[1.15] font-medium text-pure-white max-w-200">
			{heading}
		</h1>
	)

	const ctaBlock = hasCta && (
		<div className="mt-8 flex w-fit items-center gap-[1.0625rem]">
			{primaryCta && (
				<Button variant="primary" size="lg" href={primaryCta.href} isExternal={primaryCta.isExternal}>
					{primaryCta.label}
				</Button>
			)}
			{secondaryCta && (
				<Button variant="secondary-dark" size="lg" href={secondaryCta.href} isExternal={secondaryCta.isExternal}>
					{secondaryCta.label}
				</Button>
			)}
		</div>
	)

	// Gap-6 is scoped to overline/heading here — the CTA row below carries its own mt-8
	// so it doesn't inherit the same 24px gap via the outer flex container.
	const textBlock = (className: string) => (
		<div className={className}>
			<div className="flex flex-col gap-6">
				{overlineText}
				{headingText}
			</div>
			{ctaBlock}
		</div>
	)

	const collageImage1 = image1 ? (
		<figure className="size-38 max-w-full aspect-square mb-[-3.6875rem] overflow-hidden">
			<img src={image1.src} alt={image1.alt} className="w-full h-full object-cover" />
		</figure>
	) : (
		<ImageFallback className="size-38 max-w-full aspect-square mb-[-3.6875rem] overflow-hidden bg-brand-950 flex items-center justify-center" />
	)

	const collageImage2 = (
		<div className="pl-12 w-full">
			{image2 ? (
				<figure className="w-full aspect-328/180 overflow-hidden">
					<img src={image2.src} alt={image2.alt} className="w-full h-full object-cover" />
				</figure>
			) : (
				<ImageFallback className="w-full aspect-328/180 overflow-hidden bg-brand-950 flex items-center justify-center" />
			)}
		</div>
	)

	return (
		<section className="bg-black-950 py-12">
			<div className="container">
				{images.length === 2 && (
					<>
						{/* ── 2-image mobile — text first, then staggered collage below (NOT a grid) ── */}
						<div className="flex flex-col md:hidden">
							{textBlock('flex flex-col')}
							<div className="flex flex-col mt-16">
								{collageImage1}
								{collageImage2}
							</div>
						</div>

						{/* ── 2-image desktop/lg — flex row (no grid). Row aspect-656/227 = image-1's measured 454:1312 (desktop) / 232.54:672 (lg) width-to-container ratio, identical at both Figma frames — image-1 is a true % of the container, not a per-breakpoint fixed size. Row height (from that ratio) gives image-2 a definite h-full target instead of relying on stretch, which loses to image-2's own intrinsic aspect ratio ── */}
						<div className="hidden md:flex md:flex-col">
							{textBlock('flex flex-col mb-8')}
							<div className="flex gap-8 aspect-656/227 min-h-0">
								{image1 ? (
									<figure className="aspect-square h-full shrink-0 overflow-hidden">
										<img src={image1.src} alt={image1.alt} className="w-full h-full object-cover" />
									</figure>
								) : (
									<ImageFallback className="aspect-square h-full shrink-0 overflow-hidden bg-brand-950 flex items-center justify-center" />
								)}
								{image2 ? (
									<figure className="flex-1 min-w-0 h-full max-w-full overflow-hidden">
										<img src={image2.src} alt={image2.alt} className="w-full h-full object-cover" />
									</figure>
								) : (
									<ImageFallback className="flex-1 min-w-0 h-full max-w-full overflow-hidden bg-brand-950 flex items-center justify-center" />
								)}
							</div>
						</div>
					</>
				)}

				{images.length === 1 && (
					// ── 1-image — mobile: image full-width square below text; lg: both full-width stacked; desktop: text/image 6/6 side by side ──
					<div className="flex flex-col gap-16 xl:grid xl:grid-cols-12 xl:gap-8 xl:items-stretch">
						{textBlock('flex flex-col xl:col-span-6 xl:justify-center')}
						{image1 ? (
							<figure className="w-full max-w-full aspect-square max-h-122 md:max-h-none md:aspect-720/489 xl:col-span-6 xl:h-auto overflow-hidden">
								<img src={image1.src} alt={image1.alt} className="w-full h-full object-cover" />
							</figure>
						) : (
							<ImageFallback className="w-full max-w-full aspect-square max-h-122 md:max-h-none md:aspect-720/489 xl:col-span-6 xl:h-auto overflow-hidden bg-brand-950 flex items-center justify-center" />
						)}
					</div>
				)}

				{images.length === 0 && (
					// ── No-image — text only, shorter section ──
					textBlock('flex flex-col')
				)}
			</div>
		</section>
	)
}
